import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  FloatingLabel,
  Tab,
  Nav,
  Modal,
  Spinner,
  Accordion,
  ListGroup,
  Alert,
} from "react-bootstrap";
import key from "weak-key";
import {
  ChevronLeft,
  ChevronRight,
  CornerDownLeft,
  CornerDownRight,
  Plus,
  Minus,
} from "react-feather";
import { useForm, Controller } from "react-hook-form";

import { useAuth } from "../../contexts/authContext";
import { useNotification } from "../../components/notification";
import apiService from "../../services/apiService";

import { formatAmount } from "../../utils";
import { cartSession } from "../../utils/cart";

// Dynamically import the components for each view
const SUVFrontView = React.lazy(() =>
  import("../../components/car/SUV/frontView")
);
const SUVBackView = React.lazy(() =>
  import("../../components/car/SUV/backView")
);
const SUVLeftView = React.lazy(() =>
  import("../../components/car/SUV/leftView")
);
const SUVRightView = React.lazy(() =>
  import("../../components/car/SUV/rightView")
);

const HatchbackFrontView = React.lazy(() =>
  import("../../components/car/Hatchback/frontView")
);
const HatchbackBackView = React.lazy(() =>
  import("../../components/car/Hatchback/backView")
);
const HatchbackLeftView = React.lazy(() =>
  import("../../components/car/Hatchback/leftView")
);
const HatchbackRightView = React.lazy(() =>
  import("../../components/car/Hatchback/rightView")
);

const SedanFrontView = React.lazy(() =>
  import("../../components/car/Sedan/frontView")
);
const SedanBackView = React.lazy(() =>
  import("../../components/car/Sedan/backView")
);
const SedanLeftView = React.lazy(() =>
  import("../../components/car/Sedan/leftView")
);
const SedanRightView = React.lazy(() =>
  import("../../components/car/Sedan/rightView")
);

const CarCustomizer = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      carModel: 1,
      manufacturer: "",
      search: "",
    },
  });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showNotification } = useNotification();

  const [categoryId, setCategoryId] = useState("");
  const [carModels, setCarModels] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [carModel, setCarModel] = useState("Hatchback");
  const [colorGroups, setColorGroups] = useState([]);
  const [activeColorGroup, setActiveColorGroup] = useState("");
  const [defaultProducts, setDefaultProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [carView, setCarView] = useState("FRONT");
  const [primerSelected, setPrimerSelected] = useState(true);
  const [quantity, setQuantity] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [coatTypeCarModels, setCoatTypeCarModels] = useState([]);

  useEffect(() => {
    const fetchCarModels = async () => {
      try {
        setIsPageLoading(true);
        console.log(process.env.REACT_APP_API_BASE_URL);
        const response = await apiService.get(
          "/category/get-car-search-filter"
        );

        setCarModels(
          response.categories[0].defaultCategoryValue.coatType[0].models
        );
        setManufacturers(response.manufacturers);
        setCategoryId(response.categories[0].id);
        setDefaultProducts(
          response.categories[0].defaultCategoryValue.defaultProducts
        );
        setQuantity(
          response.categories[0].defaultCategoryValue.coatType[0].models[0]
            .defaultLiters
        );
        setCoatTypeCarModels(
          response.categories[0].defaultCategoryValue.coatType
        );
      } catch (error) {
        console.error("Error fetching car models:", error);
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchCarModels();
  }, []);

  // Calculate total amount whenever relevant data changes
  useEffect(() => {
    // Skip calculation if we don't have the necessary data
    if (
      !selectedProduct ||
      Object.keys(selectedProduct).length === 0 ||
      !defaultProducts ||
      defaultProducts.length === 0
    ) {
      return;
    }
    let calculatedTotal = 0;

    // Helper to calculate discounted price for a product and quantity
    const calcDiscounted = (product, qty = quantity) => {
      if (!product) return 0;
      const basePrice = (product.pricePerLiter || product.price) * qty || 0;
      const percentageDiscount = product.totalPercentageDiscount || 0;
      const fixedDiscount = product.totalFixedDiscount || 0;
      return getVolumeDiscountPrice(
        basePrice,
        product,
        product.id || product.productId,
        percentageDiscount,
        fixedDiscount,
        qty
      ).finalPrice;
    };

    // Color product
    calculatedTotal += calcDiscounted(selectedProduct);

    // Clear Coat or PU Hardener
    if (
      selectedProduct.colourType === "BASE COAT" ||
      selectedProduct.colourType === "PEARL COAT WITH BASE WHITE"
    ) {
      const clearCoat = defaultProducts.find((p) => p.name === "Clear Coat");
      const clearCoatQty =
        selectedProduct.colourType === "PEARL COAT WITH BASE WHITE"
          ? quantity / 2
          : quantity;
      calculatedTotal += calcDiscounted(clearCoat, clearCoatQty);
    } else if (selectedProduct.colourType === "TOP COAT") {
      const puHardener = defaultProducts.find((p) => p.name === "PU Hardener");
      calculatedTotal += calcDiscounted(puHardener);
    }

    // Primer
    if (primerSelected) {
      const primer = defaultProducts.find((p) => p.name === "Primer");
      const primerQty =
        selectedProduct.colourType === "PEARL COAT WITH BASE WHITE"
          ? quantity / 2
          : quantity;
      calculatedTotal += calcDiscounted(primer, primerQty);
    }

    setTotalAmount(calculatedTotal);
  }, [selectedProduct, defaultProducts, quantity, primerSelected]);

  const getVolumeDiscountPrice = (
    basePrice,
    product,
    productId,
    productFlatPercentageDiscount,
    productFlatFixedDiscount,
    quantity
  ) => {
    const discounts = product?.volumeDiscountRules?.filter(
      (d) => d.productId === productId
    );

    let totalVolumeDiscountPercentage = productFlatPercentageDiscount;
    let totalVolumeDiscountAmount = productFlatFixedDiscount;
    let finalPrice = basePrice;

    if (Array.isArray(discounts) && discounts.length > 0) {
      for (const discount of discounts) {
        const matchedRule = discount.rules.find((rule) => {
          const min = rule.minVolume ?? 0;
          const max = rule.maxVolume;
          if (typeof max === "undefined") {
            return quantity >= min; // Open-ended rule
          }
          return quantity >= min && quantity <= max;
        });

        if (!matchedRule) continue;

        const { pricePerLiter } = matchedRule;

        if (pricePerLiter && pricePerLiter > 0) {
          // Calculate total price based on quantity * pricePerLiter
          const volumeBasedPrice = quantity * pricePerLiter;

          if (volumeBasedPrice > 0) {
            totalVolumeDiscountAmount += volumeBasedPrice;
          }
        }
      }
    }

    const percentageDiscountValue =
      (basePrice * totalVolumeDiscountPercentage) / 100;

    finalPrice =
      basePrice - percentageDiscountValue - totalVolumeDiscountAmount;

    return {
      finalPrice: parseFloat(finalPrice.toFixed(2)),
      totalVolumeDiscountAmount: parseFloat(
        totalVolumeDiscountAmount.toFixed(2)
      ),
      totalVolumeDiscountPercentage: parseFloat(
        totalVolumeDiscountPercentage.toFixed(2)
      ),
    };
  };

  const handleDecrement = () => {
    if (selectedProduct.colourType === "PEARL COAT WITH BASE WHITE") {
      if (quantity > 2) {
        setQuantity(quantity - 2);
      }
    } else {
      if (quantity > 1) {
        setQuantity(quantity - 1);
      }
    }
  };

  const handleIncrement = () => {
    if (selectedProduct.colourType === "PEARL COAT WITH BASE WHITE") {
      setQuantity(quantity + 2);
    } else {
      setQuantity(quantity + 1);
    }
  };

  const onSubmit = async (data) => {
    try {
      data.categoryId = categoryId;
      setIsPageLoading(true);
      const response = await apiService.get("/products/search", data);
      setColorGroups(response);
      setActiveColorGroup(response.length > 0 ? response[0].colorGroup : "");
      setSelectedProduct(response.length > 0 ? response[0].products[0] : {});

      let colourType = response[0].products[0].colourType;

      setCarModels(
        coatTypeCarModels.find((x) => x.name === colourType)?.models || []
      );

      setQuantity(
        coatTypeCarModels
          .find((x) => x.name === colourType)
          ?.models.find((x) => x.name === carModel)?.defaultLiters || 0
      );
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setIsPageLoading(false);
    }
  };

  const renderCarView = useCallback(() => {
    const carModelViews = {
      SUV: {
        FRONT: <SUVFrontView fill={selectedProduct?.hexCode || "#000000"} />,
        BACK: <SUVBackView fill={selectedProduct?.hexCode || "#000000"} />,
        LEFT: <SUVLeftView fill={selectedProduct?.hexCode || "#000000"} />,
        RIGHT: <SUVRightView fill={selectedProduct?.hexCode || "#000000"} />,
      },
      Sedan: {
        FRONT: <SedanFrontView fill={selectedProduct?.hexCode || "#000000"} />,
        BACK: <SedanBackView fill={selectedProduct?.hexCode || "#000000"} />,
        LEFT: <SedanLeftView fill={selectedProduct?.hexCode || "#000000"} />,
        RIGHT: <SedanRightView fill={selectedProduct?.hexCode || "#000000"} />,
      },
      Hatchback: {
        FRONT: (
          <HatchbackFrontView fill={selectedProduct?.hexCode || "#000000"} />
        ),
        BACK: (
          <HatchbackBackView fill={selectedProduct?.hexCode || "#000000"} />
        ),
        LEFT: (
          <HatchbackLeftView fill={selectedProduct?.hexCode || "#000000"} />
        ),
        RIGHT: (
          <HatchbackRightView fill={selectedProduct?.hexCode || "#000000"} />
        ),
      },
    };

    return carModelViews[carModel]?.[carView] || carModelViews.SUV[carView];
  }, [carModel, carView, selectedProduct]);

  const renderTotalAmount = () => {
    return (
      <h4>
        <span className="text-primary fw-bold">
          <span className="currency">&#8377;</span>
          {formatAmount(totalAmount)}
        </span>
      </h4>
    );
  };

  const renderSavings = (percentageDiscount, fixedDiscount) => {
    if (percentageDiscount > 0 || fixedDiscount > 0) {
      return (
        <div className="text-success fw-medium mt-2">
          <span className="badge bg-primary fs-medium fw-medium">
            SAVE {percentageDiscount > 0 ? `${percentageDiscount}%` : ""}{" "}
            {percentageDiscount > 0 && fixedDiscount > 0 && " + "}
            {fixedDiscount > 0 && (
              <Fragment>
                <span className="currency">&#8377;</span>
                {fixedDiscount}
              </Fragment>
            )}
          </span>
        </div>
      );
    }
    return null;
  };

  const renderPrice = (isDefaultProduct = false, defaultProductName = "") => {
    if (
      defaultProducts?.length === 0 ||
      (!selectedProduct && !selectedProduct.name)
    )
      return null;

    const product = isDefaultProduct
      ? defaultProducts.find((p) => p.name === defaultProductName)
      : selectedProduct;

    if (!product || Object.keys(product).length === 0) return null;

    let calQuantity = quantity;
    if (
      selectedProduct.colourType === "PEARL COAT WITH BASE WHITE" &&
      isDefaultProduct
    ) {
      calQuantity = quantity / 2;
    }

    const basePrice =
      (product.price || product.pricePerLiter) * calQuantity || 0;
    const percentageDiscount = product.totalPercentageDiscount || 0;
    const fixedDiscount = product.totalFixedDiscount || 0;

    const volumeDiscounts = getVolumeDiscountPrice(
      basePrice,
      product,
      product?.id || product?.productId,
      percentageDiscount,
      fixedDiscount,
      calQuantity
    );

    const originalPrice = basePrice;
    const discountedPrice = volumeDiscounts.finalPrice;
    const totalPercentageDiscount =
      volumeDiscounts.totalVolumeDiscountPercentage;
    const totalFixedDiscount = volumeDiscounts.totalVolumeDiscountAmount;

    return (
      <div>
        {discountedPrice < originalPrice ? (
          <>
            <span className="text-decoration-line-through text-muted me-2">
              <span className="currency">&#8377;</span>
              {formatAmount(originalPrice)}
            </span>
            <span className="text-primary fw-bold">
              <span className="currency">&#8377;</span>
              {formatAmount(discountedPrice)}
            </span>
            {renderSavings(totalPercentageDiscount, totalFixedDiscount)}
          </>
        ) : (
          <span className="text-primary fw-bold">
            <span className="currency">&#8377;</span>
            {formatAmount(originalPrice)}
          </span>
        )}
      </div>
    );
  };

  const addToCart = async () => {
    const sessionId = isAuthenticated ? null : cartSession.get();
    if (!isAuthenticated && !cartSession.isExist()) {
      cartSession.save("sessionId", sessionId);
    }

    const payload = {
      productId: selectedProduct.id,
      quantity: quantity,
      sessionId: sessionId,
      carModel: carModel,
      hasPrimer: primerSelected,
      colourType: selectedProduct.colourType,
    };
    setIsSubmitting(true);
    try {
      const response = await apiService.post("/cart", payload);
      navigate("/cart");
      cartSession.save("cartId", response.id);
    } catch (error) {
      showNotification(
        "danger",
        error.message || "Failed to add product in cart.",
        5000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onChangeColour = (product) => {
    setSelectedProduct(product);

    let colourType = product.colourType;

    setCarModels(
      coatTypeCarModels.find((x) => x.name === colourType)?.models || []
    );

    setQuantity(
      coatTypeCarModels
        .find((x) => x.name === colourType)
        ?.models.find((x) => x.name === carModel)?.defaultLiters || 0
    );
  };

  return (
    <Container className="car-customizer">
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white p-3 rounded shadow-sm mt-4"
      >
        <Row>
          <Col sm={6} lg={3} className="mb-3 mb-lg-0">
            <FloatingLabel controlId="ddCarModel" label="Car Model">
              <Controller
                name="carModel"
                control={control}
                rules={{ required: "Car Model is required" }}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    aria-label="Select Car Model"
                    className={`fw-medium ${
                      errors.carModel ? "is-invalid" : ""
                    }`}
                    disabled={isPageLoading || carModels.length === 0}
                    onChange={(e) => {
                      const selectedCarModel = carModels.find(
                        (model) => model.modelId === parseInt(e.target.value)
                      );
                      console.log("Selected Car Model:", selectedCarModel);
                      setCarModel(selectedCarModel?.name);
                      setQuantity(selectedCarModel?.defaultLiters);
                      field.onChange(e);
                    }}
                    value={field.value}
                  >
                    {isPageLoading ? (
                      <option>Loading models...</option>
                    ) : (
                      <>
                        <option value="">Select a car model</option>
                        {carModels.map((model) => (
                          <option key={model.modelId} value={model.modelId}>
                            {model.name}
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                )}
              />
              {errors.carModel && (
                <div className="invalid-feedback">
                  {errors.carModel.message}
                </div>
              )}
            </FloatingLabel>
          </Col>

          <Col sm={6} lg={3} className="mb-3 mb-lg-0">
            <FloatingLabel controlId="ddOEM" label="OEM">
              <Controller
                name="manufacturer"
                control={control}
                rules={{ required: "OEM is required" }}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    aria-label="Select OEM"
                    className={`fw-medium ${
                      errors.manufacturer ? "is-invalid" : ""
                    }`}
                    disabled={isPageLoading || carModels.length === 0}
                  >
                    {isPageLoading ? (
                      <option>Loading OEM...</option>
                    ) : (
                      <>
                        <option value="">Select OEM</option>
                        {manufacturers.map((manufacturer) => (
                          <option key={manufacturer} value={manufacturer}>
                            {manufacturer}
                          </option>
                        ))}
                      </>
                    )}
                  </Form.Select>
                )}
              />
              {errors.manufacturer && (
                <div className="invalid-feedback">
                  {errors.manufacturer.message}
                </div>
              )}
            </FloatingLabel>
          </Col>

          <Col sm={6} lg={3} className="mb-3 mb-sm-0">
            <FloatingLabel controlId="txtColor" label="Color Code or Name">
              <Controller
                name="search"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="Color code or name"
                    className="fw-medium"
                  />
                )}
              />
            </FloatingLabel>
          </Col>
          <Col sm={6} lg={3}>
            <Button variant="primary" type="submit" className="w-100 h-100">
              Find it now
            </Button>
          </Col>
        </Row>
      </Form>

      <Row className="mt-5">
        <Col lg={6} className="justify-content-center align-items-center">
          {renderCarView()}
          <div className="text-center mb-4">
            <Button
              variant="light"
              size="sm"
              className="me-2 fs-6 btn-view"
              title="Front View"
              onClick={() => setCarView("FRONT")}
            >
              <CornerDownRight width={20} />
            </Button>
            <Button
              variant="light"
              size="sm"
              className="me-2 fs-6 btn-view"
              title="Left View"
              onClick={() => setCarView("LEFT")}
            >
              <ChevronLeft width={20} />
            </Button>
            <Button
              variant="light"
              size="sm"
              className="me-2 fs-6 btn-view"
              title="Right View"
              onClick={() => setCarView("RIGHT")}
            >
              <ChevronRight width={20} />
            </Button>
            <Button
              variant="light"
              size="sm"
              className="me-2 fs-6 btn-view"
              title="Back View"
              onClick={() => setCarView("BACK")}
            >
              <CornerDownLeft width={20} />
            </Button>
          </div>
        </Col>
        {colorGroups.length > 0 ? (
          <Col lg={6}>
            <div className="bg-white p-3 rounded-4 mb-4">
              <Tab.Container
                id="color-group-tabs"
                activeKey={activeColorGroup}
                onSelect={(key) => setActiveColorGroup(key)}
              >
                <Fragment>
                  <Nav
                    variant="tabs"
                    className="flex-row border-2 border-bottom"
                  >
                    {colorGroups.map((model) => (
                      <Nav.Item key={key(model)}>
                        <Nav.Link
                          eventKey={model.colorGroup}
                          className={`border-0 ${
                            activeColorGroup === model.colorGroup
                              ? "border-3 border-bottom"
                              : ""
                          }`}
                        >
                          {model.colorGroup.charAt(0).toUpperCase() +
                            model.colorGroup.slice(1)}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>

                  <Tab.Content>
                    {colorGroups.map((model) => (
                      <Tab.Pane
                        eventKey={model.colorGroup}
                        key={model.colorGroup}
                        className="py-3"
                      >
                        {model.products.map((product) => (
                          <Button
                            key={key(product)}
                            title={product.name}
                            className={`p-0 border-0 rounded-0 color-picker ${
                              product.hexCode === selectedProduct?.hexCode
                                ? "active"
                                : ""
                            }`}
                            style={{ backgroundColor: product.hexCode }}
                            onClick={() => onChangeColour(product)}
                          ></Button>
                        ))}
                      </Tab.Pane>
                    ))}
                    <Alert variant="light" className="fw-medium p-2">
                      <small>
                        *Colours shown are only a digital representation. Actual
                        colours may vary depending on various factors.
                      </small>
                    </Alert>
                  </Tab.Content>
                </Fragment>
              </Tab.Container>
            </div>
            {Object.keys(selectedProduct).length > 0 && (
              <div className="bg-white rounded-3 px-4 py-3">
                <h6>Color Specification</h6>
                <hr className="border" />
                <div className="d-flex justify-content-between">
                  <div>
                    <label className="d-block text-light-emphasis mb-1 fs-medium">
                      Name
                    </label>
                    <label className="fs-medium">{selectedProduct.name}</label>
                  </div>
                  <div className="vr"></div>
                  <div>
                    <label className="d-block text-light-emphasis mb-1 fs-medium">
                      Color Code
                    </label>
                    <label className="fs-medium">
                      {selectedProduct.colourCode}
                    </label>
                  </div>
                  <div className="vr"></div>
                  <div>
                    <label className="d-block text-light-emphasis mb-1 fs-medium">
                      Formula Code
                    </label>
                    <label className="fs-medium">
                      {selectedProduct.formulaCode}
                    </label>
                  </div>
                </div>
                {selectedProduct.formulationPerLiter?.length > 0 && (
                  <Accordion className="mt-4">
                    <Accordion.Item eventKey="0">
                      <Accordion.Header as="h6">
                        Formulation Per Liter
                      </Accordion.Header>
                      <Accordion.Body>
                        <ListGroup variant="flush">
                          {selectedProduct.formulationPerLiter.map(
                            (item, index, array) => (
                              <ListGroup.Item
                                key={index}
                                className={`d-flex justify-content-between align-items-center border-bottom bg-transparent px-0 ${
                                  index === array.length - 1
                                    ? "border-bottom-0 fw-medium"
                                    : ""
                                }`}
                              >
                                <span>{item.code}</span>
                                <span>
                                  {item["1L"] ? item["1L"].toFixed(2) : ""}
                                </span>
                              </ListGroup.Item>
                            )
                          )}
                        </ListGroup>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}
              </div>
            )}
          </Col>
        ) : (
          <Col lg={6} className="bg-white p-3 rounded-4 mb-4">
            <div className="d-flex align-items-center justify-content-center h-100 text-center py-3 text-muted">
              The palette is ready. Find the perfect color for your car! <br />{" "}
              Select a model and OEM to explore.
            </div>
          </Col>
        )}
      </Row>

      <Row>
        <Col xs={12} className="my-5">
          <h6 className="m-0">Product Summary</h6>
        </Col>
        <Col sm={12} md={4} className="mb-3 mt-md-0">
          <div className="d-flex justify-content-between align-items-center p-3 bg-white shadow-sm rounded-3 h-100">
            <div>
              <label className="d-block fw-medium mb-1">
                {selectedProduct && selectedProduct.colourType
                  ? selectedProduct.colourType
                  : "Coat"}
              </label>
              <label>
                <span className="fw-medium">{quantity}</span>{" "}
                <span className="text-light-emphasis">Liters</span>
              </label>
            </div>
            <div className="text-primary fs-6 fw-semibold text-end">
              {renderPrice()}
              {carModel === "Custom" && (
                <div className="d-inline-flex align-items-center border rounded-2 mt-1">
                  <Button
                    variant="link"
                    onClick={handleDecrement}
                    className="p-1 text-dark"
                  >
                    <Minus width={14} />
                  </Button>
                  <span className="mx-2 fs-6">{quantity}</span>
                  <Button
                    variant="link"
                    onClick={handleIncrement}
                    className="p-1 text-dark"
                  >
                    <Plus width={16} />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Col>

        <Col sm={12} md={4} className="mb-3 mt-md-0">
          <div className="d-flex justify-content-between align-items-center p-3 bg-white shadow-sm rounded-3 h-100">
            <div>
              <label className="d-block fw-medium mb-1">
                {selectedProduct && selectedProduct.colourType === "TOP COAT"
                  ? "PU Hardener(0.5 L)"
                  : "Clear Coat(1.5L)"}
              </label>
              <label>
                <span className="fw-medium">
                  {selectedProduct &&
                  selectedProduct.colourType === "PEARL COAT WITH BASE WHITE"
                    ? quantity > 0
                      ? quantity / 2
                      : 0
                    : quantity}
                </span>{" "}
                <span className="text-light-emphasis">Pack</span>
              </label>
            </div>
            <div className="text-primary fs-6 fw-semibold text-end">
              {renderPrice(
                true,
                selectedProduct && selectedProduct.colourType === "TOP COAT"
                  ? "PU Hardener"
                  : "Clear Coat"
              )}
            </div>
          </div>
        </Col>

        <Col sm={12} md={4} className="mb-3 mt-md-0">
          <div className="d-flex justify-content-between align-items-center p-3 bg-white shadow-sm rounded-3 h-100">
            <div className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                aria-label="Primer"
                checked={primerSelected}
                onChange={() => setPrimerSelected(!primerSelected)}
                className="custom-checkbox"
              />
              <div className="ps-3">
                <label className="d-block fw-medium mb-1">Primer(1L)</label>
                <label>
                  <span className="fw-medium">
                    {selectedProduct &&
                    selectedProduct.colourType === "PEARL COAT WITH BASE WHITE"
                      ? quantity > 0
                        ? quantity / 2
                        : 0
                      : quantity}
                  </span>{" "}
                  <span className="text-light-emphasis">Pack</span>
                </label>
              </div>
            </div>
            <div className="text-primary fs-6 fw-semibold text-end">
              {renderPrice(true, "Primer")}
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mt-5 mb-4">
        <Col>
          <h5 className="text-light-emphasis">Total Amount</h5>
          {renderTotalAmount()}
        </Col>
        <Col className="text-end">
          <Button
            variant="primary"
            className="px-5 py-3 rounded-0"
            onClick={addToCart}
          >
            {isSubmitting ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "Add to Cart"
            )}
          </Button>
        </Col>
      </Row>

      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        contentClassName="bg-transparent border-0"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default React.memo(CarCustomizer);
